import { RouteConfig } from 'vue-router'
import VueI18n from 'vue-i18n'
import Vue from 'vue'
import { AxiosResponse } from 'axios'

const ProductListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ProductListView.vue')
const ProductItemView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ProductItemView/ProductItemView.vue')
const ProviderReferenceListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ProviderReferenceListView.vue')
const ServiceListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ServiceListView.vue')
const ServiceItemView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ServiceItemView/ServiceItemView.vue')
const GroupListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/GroupListView.vue')
const CommandListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/CommandListView.vue')
const CommandFormView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/CommandFormView/CommandFormView.vue')
const CommandItemView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/CommandItemView/CommandItemView.vue')
const ManualMovementListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/ManualMovementListView.vue')
const MouvementListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/MouvementListView.vue')
const LotListView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/LotListView.vue')
const StockStateView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/StockStateView.vue')
const CatalogReportView: DynamicImport = () => import(/* webpackChunkName: "catalog" */ '../views/CatalogReportView.vue')

const routes: (i18n: VueI18n) => Array<RouteConfig> = (i18n) => ([
  {
    path: '/catalog/products',
    name: 'catalog-products-list',
    component: ProductListView,
    meta: {
      title: i18n.t('catalog.product.listTitle'),
      userPermission: ['catalog-products', 1],
      buttons: [
        {
          label: i18n.t('catalog.product.addLabel'),
          modal: {
            key: 'catalog-product-form',
            submitted: (response: AxiosResponse) => {
              Vue.prototype.$kwCatalog.router.push({
                name: 'catalog-product-item',
                params: {
                  id: String(response.data.id)
                }
              })
            }
          },
          userPermission: ['catalog-products', 2],
          attributes: {
            class: 'rounded'
          }
        },
        {
          label: i18n.t('catalog.providerReferences.menu'),
          to: '/catalog/providers-references',
          variant: 'secondary',
          attributes: {
            class: 'rounded ml-2'
          }
        }
      ]
    }
  },
  {
    path: '/catalog/products/:id',
    name: 'catalog-product-item',
    component: ProductItemView,
    meta: {
      title: i18n.t('catalog.product.itemTitle'),
      userPermission: ['catalog-products', 1],
      pageHeader: false
    }
  },
  {
    path: '/catalog/providers-references',
    name: 'catalog-providers-references-list',
    component: ProviderReferenceListView,
    meta: {
      title: i18n.t('catalog.providerReferences.listTitle'),
      userPermission: ['catalog-products', 1],
      buttons: [
        {
          label: i18n.t('catalog.product.listTitle'),
          to: '/catalog/products'
        }
      ]
    }
  },
  {
    path: '/catalog/services',
    name: 'catalog-services-list',
    component: ServiceListView,
    meta: {
      title: i18n.t('catalog.service.listTitle'),
      userPermission: ['catalog-services', 1],
      buttons: [
        {
          label: i18n.t('catalog.service.addLabel'),
          userPermission: ['catalog-services', 2],
          modal: {
            key: 'catalog-service-form',
            submitted: (response: AxiosResponse) => {
              Vue.prototype.$kwCatalog.router.push({
                name: 'catalog-service-item',
                params: {
                  id: String(response.data.id)
                }
              })
            }
          }
        }
      ]
    }
  },
  {
    path: '/catalog/services/:id',
    name: 'catalog-service-item',
    component: ServiceItemView,
    meta: {
      title: i18n.t('catalog.service.itemTitle'),
      userPermission: ['catalog-services', 1],
      pageHeader: false
    }
  },
  {
    path: '/catalog/groups',
    name: 'catalog-groups-list',
    component: GroupListView,
    meta: {
      title: i18n.t('catalog.group.listTitle'),
      appModule: 'catalog-groups',
      userPermission: ['catalog-groups', 1],
      buttons: [
        {
          label: i18n.t('catalog.group.addLabel'),
          modal: 'catalog-group-form',
          userPermission: ['catalog-groups', 2]
        }
      ]
    }
  },
  {
    path: '/catalog/groups/:id',
    name: 'catalog-group-item',
    component: ProductItemView,
    meta: {
      title: i18n.t('catalog.group.itemTitle'),
      appModule: 'catalog-groups',
      userPermission: ['catalog-groups', 1],
      pageHeader: false
    }
  },
  {
    path: '/catalog/report',
    name: 'catalog-report',
    component: CatalogReportView,
    meta: {
      title: 'Statistiques',
      userPermission: ['sales-invoice', 1]
    }
  },
  {
    path: '/stock/commands',
    name: 'stock-commands',
    component: CommandListView,
    meta: {
      title: i18n.t('catalog.stock.commandsListTitle'),
      appModule: 'stock',
      userPermission: ['manage-stock', 1],
      buttons: [
        {
          label: i18n.t('catalog.stock.addLabel'),
          to: '/stock/commands/form/'
        }
      ]
    }
  },
  {
    path: '/stock/commands/form/:id?',
    name: 'stock-command-form',
    component: CommandFormView,
    meta: {
      appModule: 'stock',
      userPermission: ['manage-stock', 1],
      sidebarMode: 2,
      allowFullscreen: true,
      pageHeader: false
    }
  },
  {
    path: '/stock/commands/:id',
    name: 'stock-command-item',
    component: CommandItemView,
    meta: {
      appModule: 'stock',
      userPermission: ['manage-stock', 1],
      pageHeader: false,
      appClass: { 'stock-command-item': true }
    }
  },
  {
    path: '/stock/state',
    name: 'stock-state',
    component: StockStateView,
    meta: {
      appModule: 'stock',
      userPermission: ['manage-stock', 1],
      title: i18n.t('catalog.stock.StockStatePagetitle'),
      buttons: [
        {
          label: i18n.t('catalog.stock.addManual'),
          modal: 'stock-manual-form'
        },
        {
          label: i18n.t('catalog.stock.addLabel'),
          to: '/stock/commands/form/'
        }
      ]
    }
  },
  {
    path: '/stock/manual-mouvements',
    name: 'stock-manual-mouvements',
    component: ManualMovementListView,
    meta: {
      title: i18n.t('catalog.stock.manualMvtListPagetitle'),
      appModule: 'stock',
      userPermission: ['manage-stock', 1],
      buttons: [
        {
          label: i18n.t('catalog.stock.addManual'),
          modal: 'stock-manual-form'
        }
      ]
    }
  },
  {
    path: '/stock/mouvements',
    name: 'stock-mouvements',
    component: MouvementListView,
    meta: {
      title: i18n.t('catalog.stock.mvtListPagetitle'),
      appModule: 'stock',
      userPermission: ['see-stock', 1]
    }
  },
  {
    path: '/stock/lots',
    name: 'stock-lots',
    component: LotListView,
    meta: {
      title: i18n.t('catalog.productLot.pageTitle'),
      appModule: 'stock',
      userPermission: ['see-stock', 1]
    }
  }
])

export default routes
